import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CreditCard, Download, ExternalLink, LogOut, Mail, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PAGE_DEFAULTS, PAGE_LIST } from "@/pages/PolicyPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-color.png`;
const LOGO_WHITE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-white.png`;

const SECTIONS = {
  fixtures: { label: "Fixtures", blank: { day: "", team_a: "", team_b: "", venue: "Kakinada District Stadium", date: "TBA", status: "UPCOMING" }, fields: [["day", "Day"], ["team_a", "Team A"], ["team_b", "Team B"], ["venue", "Venue"], ["date", "Date"], ["status", "Status"]] },
  standings: { label: "Points table", blank: { pos: 1, team: "", short: "", p: 0, w: 0, l: 0, pts: 0 }, fields: [["pos", "#", "number"], ["team", "Team"], ["short", "Short"], ["p", "P", "number"], ["w", "W", "number"], ["l", "L", "number"], ["pts", "PTS", "number"]] },
  teams: { label: "Teams", blank: { name: "", short: "", colour: "#024ea7", tag: "", logo: "" }, fields: [["name", "Name"], ["short", "Short"], ["colour", "Colour", "color"], ["tag", "Tagline"], ["logo", "Logo URL"]] },
  players: { label: "Players", blank: { name: "", role: "", stat: "" }, fields: [["name", "Name"], ["role", "Role"], ["stat", "Stat"]] },
  news: { label: "News", blank: { category: "", title: "", date: "" }, fields: [["category", "Category"], ["title", "Title"], ["date", "Date"]] },
};

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div className="field"><label>{label}</label><input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} data-testid={testid} /></div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [regs, setRegs] = useState(null);
  const [eoi, setEoi] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [gKind, setGKind] = useState("photo");
  const [gTitle, setGTitle] = useState("");
  const [gFile, setGFile] = useState(null);
  const [gBusy, setGBusy] = useState(false);
  const [gError, setGError] = useState("");
  const [pageSlug, setPageSlug] = useState("privacy");
  const [matches, setMatches] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [commText, setCommText] = useState({});
  const [newMatch, setNewMatch] = useState({ team_a: "", team_b: "", venue: "Kakinada District Stadium", date: "TBA", time: "", match_no: "", mtype: "League" });
  const [scorers, setScorers] = useState([]);
  const [newScorer, setNewScorer] = useState({ name: "", email: "", password: "" });
  const [scorerMsg, setScorerMsg] = useState("");
  const [auditFor, setAuditFor] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamSel, setTeamSel] = useState("");
  const [newPlayer, setNewPlayer] = useState({ name: "", jersey: "", role: "", category: "", price: "" });
  const [teamMsg, setTeamMsg] = useState("");
  const [tab, setTab] = useState("basics");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      axios.get(`${API}/admin/kpl`).then((r) => setContent(r.data)).catch(() => {});
      axios.get(`${API}/admin/registrations`).then((r) => setRegs(r.data)).catch(() => {});
      axios.get(`${API}/admin/eoi`).then((r) => setEoi(r.data)).catch(() => {});
      axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => {});
      axios.get(`${API}/matches`).then((r) => { setMatches(r.data); setDrafts(Object.fromEntries(r.data.matches.map((m) => [m.id, { ...m }]))); }).catch(() => {});
      axios.get(`${API}/admin/scorers`).then((r) => setScorers(r.data.scorers || [])).catch(() => {});
      axios.get(`${API}/teams`).then((r) => setTeams(r.data.teams || [])).catch(() => {});
    }
  }, [user]);

  if (!user || user.role !== "admin") return null;
  if (!content) return <div className="admin-loading" data-testid="admin-loading">Loading dashboard…</div>;

  const setScalar = (key, value) => setContent((c) => ({ ...c, [key]: value }));
  const setPageField = (slug, key, value) => setContent((c) => ({ ...c, pages: { ...(c.pages || {}), [slug]: { ...(c.pages?.[slug] || PAGE_DEFAULTS[slug]), [key]: value } } }));
  const setNextMatch = (key, value) => setContent((c) => ({ ...c, next_match: { ...c.next_match, [key]: value } }));
  const setRow = (section, index, key, value) => setContent((c) => ({ ...c, [section]: c[section].map((row, i) => (i === index ? { ...row, [key]: value } : row)) }));
  const addRow = (section) => setContent((c) => ({ ...c, [section]: [...c[section], { ...SECTIONS[section].blank }] }));
  const removeRow = (section, index) => setContent((c) => ({ ...c, [section]: c[section].filter((_, i) => i !== index) }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/kpl`, content);
      setContent(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {}
    setBusy(false);
  };

  const downloadExcel = async () => {
    const response = await axios.get(`${API}/admin/registrations/download`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = "kpl-player-registrations.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadEoiExcel = async () => {
    const response = await axios.get(`${API}/admin/eoi/download`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = "kpl-franchise-eoi.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadIdCard = async (userId, regId, btn) => {
    btn.disabled = true;
    try {
      const response = await axios.get(`${API}/admin/registrations/${userId}/idcard`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `KPL-IDCard-${regId || "player"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {}
    btn.disabled = false;
  };

  const refreshRegs = () => axios.get(`${API}/admin/registrations`).then((r) => setRegs(r.data)).catch(() => {});
  const refreshEoi = () => axios.get(`${API}/admin/eoi`).then((r) => setEoi(r.data)).catch(() => {});
  const refreshGallery = () => axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => {});

  const uploadGalleryItem = async (event) => {
    event.preventDefault();
    if (!gFile) { setGError("Choose a file to upload."); return; }
    setGBusy(true);
    setGError("");
    try {
      const body = new FormData();
      body.append("kind", gKind);
      body.append("title", gTitle);
      body.append("file", gFile);
      await axios.post(`${API}/admin/gallery`, body);
      setGTitle("");
      setGFile(null);
      event.target.reset();
      refreshGallery();
    } catch (err) {
      setGError(err.response?.data?.detail || "Upload failed — try again.");
    }
    setGBusy(false);
  };

  const deleteGalleryItem = async (itemId) => {
    await axios.delete(`${API}/admin/gallery/${itemId}`).catch(() => {});
    refreshGallery();
  };

  const refreshMatches = () => axios.get(`${API}/matches`).then((r) => {
    setMatches(r.data);
    setDrafts((d) => {
      const next = { ...d };
      r.data.matches.forEach((m) => { if (!next[m.id]) next[m.id] = { ...m }; });
      return next;
    });
  }).catch(() => {});

  const setDraft = (id, key, value) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: value } }));
  const setDraftInn = (id, inn, key, value) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [inn]: { ...d[id][inn], [key]: value } } }));

  const createMatch = async (event) => {
    event.preventDefault();
    if (!newMatch.team_a || !newMatch.team_b || newMatch.team_a === newMatch.team_b) return;
    await axios.post(`${API}/admin/matches`, newMatch).then(() => {
      setNewMatch({ team_a: "", team_b: "", venue: "Kakinada District Stadium", date: "TBA", time: "", match_no: "", mtype: "League" });
      refreshMatches();
    }).catch(() => {});
  };

  const saveMatch = async (id) => {
    const d = drafts[id];
    const payload = { team_a: d.team_a, team_b: d.team_b, venue: d.venue, date: d.date, time: d.time || "", match_no: d.match_no || "", mtype: d.mtype || "League", status: d.status, inn1: d.inn1, inn2: d.inn2, result: d.result, winner: d.winner || "", potm: d.potm || "" };
    await axios.put(`${API}/admin/matches/${id}`, payload).then((r) => setDrafts((dr) => ({ ...dr, [id]: { ...r.data.match } }))).catch(() => {});
  };

  const deleteMatch = async (id) => {
    await axios.delete(`${API}/admin/matches/${id}`).catch(() => {});
    refreshMatches();
  };

  const addCommentary = async (id) => {
    const text = (commText[id] || "").trim();
    if (!text) return;
    await axios.post(`${API}/admin/matches/${id}/commentary`, { text }).then((r) => {
      setCommText((c) => ({ ...c, [id]: "" }));
      setDrafts((d) => ({ ...d, [id]: { ...d[id], commentary: [r.data.entry, ...(d[id].commentary || [])] } }));
    }).catch(() => {});
  };

  const uploadCard = async (id, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    await axios.post(`${API}/admin/matches/${id}/card`, body).then(() => refreshMatches()).catch(() => {});
  };

  const refreshScorers = () => axios.get(`${API}/admin/scorers`).then((r) => setScorers(r.data.scorers || [])).catch(() => {});

  const createScorer = async (event) => {
    event.preventDefault();
    setScorerMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/scorers`, newScorer);
      setScorerMsg(`Scorer ${data.email} created — share the password with them directly.`);
      setNewScorer({ name: "", email: "", password: "" });
      refreshScorers();
    } catch (err) {
      setScorerMsg(err.response?.data?.detail || "Could not create scorer.");
    }
  };

  const toggleScorer = async (matchId, scorerId) => {
    const current = (matches?.matches.find((m) => m.id === matchId)?.scorers) || [];
    const next = current.includes(scorerId) ? current.filter((s) => s !== scorerId) : [...current, scorerId];
    await axios.put(`${API}/admin/matches/${matchId}/scorers`, { user_ids: next }).then(() => refreshMatches()).catch(() => {});
  };

  const showAudit = async (id) => {
    if (auditFor === id) { setAuditFor(null); return; }
    const { data } = await axios.get(`${API}/admin/matches/${id}/audit`).catch(() => ({ data: { entries: [] } }));
    setAuditEntries(data.entries || []);
    setAuditFor(id);
  };

  const deleteReg = async (userId) => {
    await axios.delete(`${API}/admin/registrations/${userId}`).catch(() => {});
    refreshRegs();
  };

  const deleteEoi = async (index) => {
    await axios.delete(`${API}/admin/eoi/${index}`).catch(() => {});
    refreshEoi();
  };

  const resendConfirmations = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/registrations/resend-whatsapp`);
      setResendMsg(`Done — WhatsApp: ${data.whatsapp_sent}/${data.total}, Email: ${data.email_sent}/${data.total}`);
      setTimeout(() => setResendMsg(""), 5000);
    } catch {
      setResendMsg("Resend failed — try again.");
    }
    setBusy(false);
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const readOnlyTab = tab === "registrations" || tab === "eoi" || tab === "gallery" || tab === "scores" || tab === "franchises";

  const refreshTeams = () => axios.get(`${API}/teams`).then((r) => setTeams(r.data.teams || [])).catch(() => {});

  const saveTeamField = async (team, key, value) => {
    const payload = { name: team.name, short: team.short, colour: team.colour, colour2: team.colour2 || "", owner: team.owner || "", coach: team.coach || "", staff: team.staff || "", [key]: value };
    await axios.put(`${API}/admin/teams/${team.id}`, payload).then((r) => setTeams((ts) => ts.map((t) => (t.id === team.id ? r.data.team : t)))).catch(() => {});
  };

  const uploadTeamMedia = async (teamId, kind, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setTeamMsg("");
    try {
      await axios.post(`${API}/admin/teams/${teamId}/media/${kind}`, body);
      refreshTeams();
      setTeamMsg("Uploaded.");
      setTimeout(() => setTeamMsg(""), 2500);
    } catch (err) { setTeamMsg(err.response?.data?.detail || "Upload failed."); }
  };

  const addPlayer = async (teamId) => {
    if (!newPlayer.name || newPlayer.name.length < 2) return;
    await axios.post(`${API}/admin/teams/${teamId}/players`, newPlayer).then(() => { setNewPlayer({ name: "", jersey: "", role: "", category: "", price: "" }); refreshTeams(); }).catch(() => {});
  };

  const deletePlayer = async (teamId, playerId) => {
    await axios.delete(`${API}/admin/teams/${teamId}/players/${playerId}`).then(() => refreshTeams()).catch(() => {});
  };

  const uploadPlayerPhoto = async (teamId, playerId, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    await axios.post(`${API}/admin/teams/${teamId}/players/${playerId}/photo`, body).then(() => refreshTeams()).catch(() => {});
  };

  const importCsv = async (teamId, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setTeamMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/teams/${teamId}/squad-csv`, body);
      setTeamMsg(data.message);
      refreshTeams();
    } catch { setTeamMsg("CSV import failed — check the file format."); }
  };

  const uploadSponsor = async (matchId, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    await axios.post(`${API}/admin/matches/${matchId}/sponsor`, body).then(() => refreshMatches()).catch(() => {});
  };

  const setCardMode = async (matchId, mode) => {
    await axios.post(`${API}/admin/matches/${matchId}/card-settings`, { card_mode: mode }).then(() => refreshMatches()).catch(() => {});
  };

  return <div className="admin-wrap" data-testid="admin-dashboard">
    <div className="admin-head"><div className="container admin-head-inner">
      <img src={LOGO_WHITE_URL} alt="KPL logo" />
      <h1>Content dashboard</h1>
      <div className="admin-head-actions">
        <Link to="/" data-testid="admin-view-site"><ExternalLink size={14} /> View site</Link>
        <button onClick={handleLogout} data-testid="admin-logout-button"><LogOut size={14} /> Log out</button>
      </div>
    </div></div>
    <div className="container">
      <div className="admin-tabs">
        <button className={tab === "basics" ? "active" : ""} onClick={() => setTab("basics")} data-testid="tab-basics">Basics</button>
        <button className={tab === "franchises" ? "active" : ""} onClick={() => setTab("franchises")} data-testid="tab-franchises">Franchises</button>
        {Object.entries(SECTIONS).map(([key, section]) => <button key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)} data-testid={`tab-${key}`}>{section.label}</button>)}
        <button className={tab === "registrations" ? "active" : ""} onClick={() => setTab("registrations")} data-testid="tab-registrations">Registrations{regs ? ` (${regs.count})` : ""}</button>
        <button className={tab === "eoi" ? "active" : ""} onClick={() => setTab("eoi")} data-testid="tab-eoi">Franchise EOI{eoi ? ` (${eoi.count})` : ""}</button>
        <button className={tab === "gallery" ? "active" : ""} onClick={() => setTab("gallery")} data-testid="tab-gallery">Gallery{gallery ? ` (${gallery.count})` : ""}</button>
        <button className={tab === "pages" ? "active" : ""} onClick={() => setTab("pages")} data-testid="tab-pages">Pages</button>
        <button className={tab === "scores" ? "active" : ""} onClick={() => setTab("scores")} data-testid="tab-scores">Scores{matches && matches.count > 0 ? ` (${matches.count})` : ""}</button>
      </div>
      <div className="admin-body">
        {tab === "basics" && <div className="admin-card">
          <div className="admin-grid">
            <Field label="Season label" value={content.season} onChange={(v) => setScalar("season", v)} testid="edit-season" />
            <Field label="Ticker status" value={content.status} onChange={(v) => setScalar("status", v)} testid="edit-status" />
            <Field label="Next up — first line" value={content.next_match.team_a} onChange={(v) => setNextMatch("team_a", v)} testid="edit-next-a" />
            <Field label="Next up — second line" value={content.next_match.team_b} onChange={(v) => setNextMatch("team_b", v)} testid="edit-next-b" />
            <Field label="Next up — date" value={content.next_match.date} onChange={(v) => setNextMatch("date", v)} testid="edit-next-date" />
            <Field label="Next up — venue" value={content.next_match.venue} onChange={(v) => setNextMatch("venue", v)} testid="edit-next-venue" />
            <Field label="Auction countdown (date & time)" type="datetime-local" value={(content.auction_date || "").slice(0, 16)} onChange={(v) => setScalar("auction_date", v ? `${v}:00+05:30` : "")} testid="edit-auction-date" />
            <Field label="Registration closes on" type="datetime-local" value={(content.registration_close || "").slice(0, 16)} onChange={(v) => setScalar("registration_close", v ? `${v}:00+05:30` : "")} testid="edit-registration-close" />
            <Field label="Bidding opens (date & time)" type="datetime-local" value={(content.bid_start || "").slice(0, 16)} onChange={(v) => setScalar("bid_start", v ? `${v}:00+05:30` : "")} testid="edit-bid-start" />
            <Field label="Bidding closes (date & time)" type="datetime-local" value={(content.bid_end || "").slice(0, 16)} onChange={(v) => setScalar("bid_end", v ? `${v}:00+05:30` : "")} testid="edit-bid-end" />
          </div>
        </div>}
        {!readOnlyTab && tab !== "basics" && tab !== "pages" && <>
          {content[tab].map((row, index) => <div className="admin-row" key={index} data-testid={`edit-${tab}-row-${index}`}>
            <button className="row-del" onClick={() => removeRow(tab, index)} data-testid={`delete-${tab}-row-${index}`} aria-label="Delete row"><Trash2 size={15} /></button>
            <div className="admin-grid">
              {SECTIONS[tab].fields.map(([key, label, type]) => <Field key={key} label={label} type={type} value={row[key]} onChange={(v) => setRow(tab, index, key, v)} testid={`edit-${tab}-${index}-${key}`} />)}
            </div>
          </div>)}
          <button className="add-row" onClick={() => addRow(tab)} data-testid={`add-${tab}-row`}><Plus size={15} /> Add {SECTIONS[tab].label.toLowerCase()} row</button>
        </>}
        {tab === "franchises" && <div className="admin-card" data-testid="franchises-panel">
          <div className="reg-head">
            <h3>Franchise teams · {teams.length}</h3>
            <div className="page-pills" style={{ margin: 0 }}>{teams.map((t) => <button key={t.id} className={`gallery-tab ${teamSel === t.id ? "active" : ""}`} onClick={() => setTeamSel(teamSel === t.id ? "" : t.id)} data-testid={`team-pill-${t.short}`}>{t.short}</button>)}</div>
          </div>
          {teamMsg && <p className="reg-empty" data-testid="team-msg">{teamMsg}</p>}
          {(teamSel ? teams.filter((t) => t.id === teamSel) : teams).map((t) => <div className="reg-row team-admin-row" key={t.id} data-testid={`team-row-${t.short}`}>
            <div className="match-row-head">
              <b>{t.name} <span style={{ color: t.colour }}>({t.short})</span></b>
              <span className="reg-id">{t.squad?.length || 0} players</span>
            </div>
            <div className="inn-grid">
              <div className="field"><label>Logo</label>
                <input type="file" accept="image/*" onChange={(e) => uploadTeamMedia(t.id, "logo", e.target.files[0])} data-testid={`team-logo-${t.short}`} />
                {t.logo_url && <img className="reg-photo" src={`${API.replace("/api", "")}${t.logo_url}`} alt="logo" data-testid={`team-logo-preview-${t.short}`} />}
              </div>
              <div className="field"><label>Banner</label>
                <input type="file" accept="image/*" onChange={(e) => uploadTeamMedia(t.id, "banner", e.target.files[0])} data-testid={`team-banner-${t.short}`} />
                {t.banner_url && <img className="reg-photo" src={`${API.replace("/api", "")}${t.banner_url}`} alt="banner" data-testid={`team-banner-preview-${t.short}`} />}
              </div>
            </div>
            <div className="inn-grid inn-grid-two">
              <div className="field"><label>Primary colour</label><input type="color" value={t.colour || "#024ea7"} onChange={(e) => setTeams((ts) => ts.map((x) => x.id === t.id ? { ...x, colour: e.target.value } : x))} onBlur={(e) => saveTeamField(t, "colour", e.target.value)} data-testid={`team-colour-${t.short}`} /></div>
              <div className="field"><label>Owner</label><input type="text" value={t.owner || ""} onChange={(e) => setTeams((ts) => ts.map((x) => x.id === t.id ? { ...x, owner: e.target.value } : x))} onBlur={(e) => saveTeamField(t, "owner", e.target.value)} data-testid={`team-owner-${t.short}`} /></div>
            </div>
            <div className="inn-grid inn-grid-two">
              <div className="field"><label>Coach</label><input type="text" value={t.coach || ""} onChange={(e) => setTeams((ts) => ts.map((x) => x.id === t.id ? { ...x, coach: e.target.value } : x))} onBlur={(e) => saveTeamField(t, "coach", e.target.value)} data-testid={`team-coach-${t.short}`} /></div>
              <div className="field"><label>Support staff</label><input type="text" value={t.staff || ""} onChange={(e) => setTeams((ts) => ts.map((x) => x.id === t.id ? { ...x, staff: e.target.value } : x))} onBlur={(e) => saveTeamField(t, "staff", e.target.value)} data-testid={`team-staff-${t.short}`} /></div>
            </div>
            <div className="field"><label>Bulk squad import — CSV with columns: name, jersey, role, category, price</label>
              <input type="file" accept=".csv" onChange={(e) => importCsv(t.id, e.target.files[0])} data-testid={`team-csv-${t.short}`} />
            </div>
            <div className="squad-list">
              {(t.squad || []).map((p) => <div className="squad-player" key={p.id} data-testid={`squad-player-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                {p.photo_url ? <img className="squad-photo" src={`${API.replace("/api", "")}${p.photo_url}`} alt={p.name} /> : <div className="squad-photo squad-photo-empty">{p.name.slice(0, 1)}</div>}
                <b>{p.name}</b>
                {p.jersey && <span className="reg-id">#{p.jersey}</span>}
                <span>{p.role || "player"}{p.category ? ` · ${p.category}` : ""}{p.price ? ` · ₹${p.price}` : ""}</span>
                <label className="linklike">Photo<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadPlayerPhoto(t.id, p.id, e.target.files[0])} data-testid={`player-photo-${p.id}`} /></label>
                <button className="row-del" style={{ position: "static" }} onClick={() => deletePlayer(t.id, p.id)} data-testid={`player-del-${p.id}`} aria-label="Remove player"><Trash2 size={13} /></button>
              </div>)}
            </div>
            <div className="add-player-grid">
              <div className="field"><label>Add player name</label><input type="text" value={newPlayer.name} onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })} data-testid={`new-player-name-${t.short}`} /></div>
              <div className="field"><label>Jersey</label><input type="text" value={newPlayer.jersey} onChange={(e) => setNewPlayer({ ...newPlayer, jersey: e.target.value })} data-testid={`new-player-jersey-${t.short}`} /></div>
              <div className="field"><label>Role</label><select value={newPlayer.role} onChange={(e) => setNewPlayer({ ...newPlayer, role: e.target.value })} data-testid={`new-player-role-${t.short}`}><option value="">—</option><option value="batsman">Batsman</option><option value="bowler">Bowler</option><option value="allrounder">All-rounder</option><option value="keeper">Wicket-keeper</option></select></div>
              <div className="field"><label>Price</label><input type="text" value={newPlayer.price} onChange={(e) => setNewPlayer({ ...newPlayer, price: e.target.value })} placeholder="e.g. 1.5 L" data-testid={`new-player-price-${t.short}`} /></div>
              <button className="button button-outline" onClick={() => addPlayer(t.id)} data-testid={`add-player-${t.short}`}><Plus size={14} /> Add player</button>
            </div>
          </div>)}
        </div>}
        {tab === "registrations" && <div className="admin-card">
          <div className="reg-head">
            <h3>Player registrations {regs ? `· ${regs.count}` : ""}</h3>
            <div className="reg-head-actions">
              <button className="button button-outline" onClick={resendConfirmations} disabled={busy} data-testid="resend-confirmations-button"><Mail size={15} /> Resend confirmations</button>
              <button className="button button-red" onClick={downloadExcel} data-testid="download-registrations-button"><Download size={15} /> Download Excel</button>
            </div>
          </div>
          {resendMsg && <p className="reg-empty" data-testid="resend-message">{resendMsg}</p>}
          {!regs || regs.count === 0 ? <p className="reg-empty" data-testid="registrations-empty">No player registrations yet.</p> : (
            <div>{regs.registrations.map((r, index) => <div className="reg-row" key={index} data-testid={`reg-row-${index}`}>
              <button className="row-del" onClick={() => deleteReg(r.user_id)} data-testid={`delete-reg-${index}`} aria-label="Delete registration"><Trash2 size={15} /></button>
              <div><label>Player Name</label><b>{r.name}</b>{r.registration_id && <span className="reg-id">{r.registration_id}</span>}</div>
              <div><label>City</label><span>{r.city}</span></div>
              <div><label>Mobile</label><span>{r.mobile}</span></div>
              <div><label>Dress Measurements</label><span>{r.dress_measurements}</span></div>
              <div><label>CricHeroes Profile</label><a href={r.cricheros_link} target="_blank" rel="noreferrer">{r.cricheros_link}</a></div>
              <div><label>Role</label><span>{r.role ? `${r.role}${r.role_detail ? ` · ${r.role_detail}` : ""}` : "—"}</span></div>
              <div><label>Levels (Year)</label><span>{[["Ranji", r.ranji_year], ["State", r.state_year], ["Zone", r.zone_year], ["District", r.district_year]].filter(([, y]) => y).map(([l, y]) => `${l} ${y}`).join(", ") || "—"}</span></div>
              <div><label>Photo</label>{r.photo_path ? <img className="reg-photo" src={`${API}/files/${r.photo_path}`} alt={`${r.name} passport`} /> : <span>—</span>}</div>
              <div><label>ID Card</label><button className="linklike" onClick={(e) => downloadIdCard(r.user_id, r.registration_id, e.currentTarget)} data-testid={`idcard-reg-${index}`}><CreditCard size={14} /> Download PDF</button></div>
              <div><label>Registered On</label><span>{(r.created_at || "").slice(0, 10)}</span></div>
            </div>)}</div>
          )}
        </div>}
        {tab === "eoi" && <div className="admin-card">
          <div className="reg-head">
            <h3>Franchise EOI submissions {eoi ? `· ${eoi.count}` : ""}</h3>
            <button className="button button-red" onClick={downloadEoiExcel} data-testid="download-eoi-button"><Download size={15} /> Download Excel</button>
          </div>
          {!eoi || eoi.count === 0 ? <p className="reg-empty" data-testid="eoi-empty">No franchise interest submissions yet.</p> : (
            <div>{eoi.submissions.map((s, index) => <div className="reg-row" key={index} data-testid={`eoi-row-${index}`}>
              <button className="row-del" onClick={() => deleteEoi(index)} data-testid={`delete-eoi-${index}`} aria-label="Delete submission"><Trash2 size={15} /></button>
              <div><label>Name</label><b>{s.name}</b></div>
              <div><label>Mobile</label><span>{s.mobile}</span></div>
              <div><label>Email</label><span>{s.email || "—"}</span></div>
              <div><label>City</label><span>{s.city}</span></div>
              <div><label>Message</label><span>{s.message || "—"}</span></div>
              <div><label>Application</label>{s.application_path ? <a href={`${API}/files/${s.application_path}`} target="_blank" rel="noreferrer" data-testid={`eoi-application-${index}`}><ExternalLink size={13} /> View filled PDF</a> : <span>—</span>}</div>
              <div><label>Submitted On</label><span>{(s.created_at || "").slice(0, 10)}</span></div>
            </div>)}</div>
          )}
        </div>}
        {tab === "scores" && <div className="admin-card">
          <div className="reg-head"><h3>Live scoreboard {matches ? `· ${matches.count}` : ""}</h3></div>
          <form className="gallery-upload" onSubmit={createMatch} data-testid="match-add-form">
            <div className="field"><label>Team A</label><select value={newMatch.team_a} onChange={(e) => setNewMatch((m) => ({ ...m, team_a: e.target.value }))} data-testid="match-new-team-a"><option value="">Select team</option>{teams.map((t) => <option key={t.id} value={t.name}>{t.logo_url ? "● " : ""}{t.name}</option>)}</select>
              {newMatch.team_a && teams.find((t) => t.name === newMatch.team_a)?.logo_url && <img className="reg-photo" src={`${API.replace("/api", "")}${teams.find((t) => t.name === newMatch.team_a).logo_url}`} alt="" />}
            </div>
            <div className="field"><label>Team B</label><select value={newMatch.team_b} onChange={(e) => setNewMatch((m) => ({ ...m, team_b: e.target.value }))} data-testid="match-new-team-b"><option value="">Select team</option>{teams.filter((t) => t.name !== newMatch.team_a).map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}</select>
              {newMatch.team_b && teams.find((t) => t.name === newMatch.team_b)?.logo_url && <img className="reg-photo" src={`${API.replace("/api", "")}${teams.find((t) => t.name === newMatch.team_b).logo_url}`} alt="" />}
            </div>
            <div className="field"><label>Match no.</label><input type="text" value={newMatch.match_no || ""} onChange={(e) => setNewMatch((m) => ({ ...m, match_no: e.target.value }))} placeholder="e.g. 7" data-testid="match-new-no" /></div>
            <div className="field"><label>Type</label><select value={newMatch.mtype || "League"} onChange={(e) => setNewMatch((m) => ({ ...m, mtype: e.target.value }))} data-testid="match-new-type"><option value="League">League</option><option value="Qualifier">Qualifier</option><option value="Eliminator">Eliminator</option><option value="Final">Final</option></select></div>
            <div className="field"><label>Venue</label><input type="text" value={newMatch.venue} onChange={(e) => setNewMatch((m) => ({ ...m, venue: e.target.value }))} data-testid="match-new-venue" /></div>
            <div className="field"><label>Date</label><input type="text" value={newMatch.date} onChange={(e) => setNewMatch((m) => ({ ...m, date: e.target.value }))} placeholder="e.g. 28 Nov" data-testid="match-new-date" /></div>
            <div className="field"><label>Time</label><input type="text" value={newMatch.time || ""} onChange={(e) => setNewMatch((m) => ({ ...m, time: e.target.value }))} placeholder="e.g. 7:00 PM" data-testid="match-new-time" /></div>
            <button className="button button-red" type="submit" data-testid="match-add-button">Add match</button>
          </form>
          <form className="gallery-upload scorer-create" onSubmit={createScorer} data-testid="scorer-create-form">
            <div className="field"><label>New scorer name</label><input type="text" value={newScorer.name} onChange={(e) => setNewScorer({ ...newScorer, name: e.target.value })} data-testid="scorer-new-name" /></div>
            <div className="field"><label>Scorer email</label><input type="email" value={newScorer.email} onChange={(e) => setNewScorer({ ...newScorer, email: e.target.value })} data-testid="scorer-new-email" /></div>
            <div className="field"><label>Password</label><input type="text" value={newScorer.password} onChange={(e) => setNewScorer({ ...newScorer, password: e.target.value })} data-testid="scorer-new-password" /></div>
            <button className="button button-outline" type="submit" data-testid="scorer-create-button">Create scorer login</button>
          </form>
          {scorerMsg && <p className="reg-empty" data-testid="scorer-msg" style={{ marginTop: 8 }}>{scorerMsg}</p>}
          {!matches || matches.count === 0 ? <p className="reg-empty" data-testid="matches-empty" style={{ marginTop: 16 }}>No matches yet — add one above to start scoring.</p> : (
            <div style={{ marginTop: 16 }}>{matches.matches.map((m, index) => { const d = drafts[m.id] || m; return <div className="reg-row match-admin-row" key={m.id} data-testid={`match-row-${index}`}>
              <button className="row-del" onClick={() => deleteMatch(m.id)} data-testid={`delete-match-${index}`} aria-label="Delete match"><Trash2 size={15} /></button>
              <div className="match-row-head"><b>{m.team_a} vs {m.team_b}</b>
                <div className="page-pills" style={{ margin: 0 }}>{["upcoming", "live", "completed", "postponed", "cancelled"].map((s) => <button type="button" key={s} className={`gallery-tab ${d.status === s ? "active" : ""}`} onClick={() => setDraft(m.id, "status", s)} data-testid={`match-status-${index}-${s}`}>{s}</button>)}</div>
              </div>
              {[["inn1", "Innings 1"], ["inn2", "Innings 2"]].map(([inn, label]) => <div className="inn-grid" key={inn}>
                <div className="field"><label>{label} — batting</label><select value={d[inn].batting} onChange={(e) => setDraftInn(m.id, inn, "batting", e.target.value)} data-testid={`match-${inn}-batting-${index}`}><option value="">—</option><option value={m.team_a}>{m.team_a}</option><option value={m.team_b}>{m.team_b}</option></select></div>
                <div className="field"><label>Runs</label><input type="number" min="0" value={d[inn].runs} onChange={(e) => setDraftInn(m.id, inn, "runs", Number(e.target.value))} data-testid={`match-${inn}-runs-${index}`} /></div>
                <div className="field"><label>Wickets</label><input type="number" min="0" max="10" value={d[inn].wickets} onChange={(e) => setDraftInn(m.id, inn, "wickets", Number(e.target.value))} data-testid={`match-${inn}-wickets-${index}`} /></div>
                <div className="field"><label>Overs</label><input type="text" value={d[inn].overs} onChange={(e) => setDraftInn(m.id, inn, "overs", e.target.value)} placeholder="19.3" data-testid={`match-${inn}-overs-${index}`} /></div>
              </div>)}
              <div className="field"><label>Result (shown when completed)</label><input type="text" value={d.result || ""} onChange={(e) => setDraft(m.id, "result", e.target.value)} placeholder="e.g. Kakinada Coastal Kings won by 12 runs" data-testid={`match-result-${index}`} /></div>
              <div className="inn-grid inn-grid-two">
                <div className="field"><label>Winner (auto-updates points table)</label><select value={d.winner || ""} onChange={(e) => setDraft(m.id, "winner", e.target.value)} data-testid={`match-winner-${index}`}><option value="">—</option><option value={m.team_a}>{m.team_a}</option><option value={m.team_b}>{m.team_b}</option><option value="no_result">No result (1 pt each)</option></select></div>
                <div className="field"><label>Player of the match</label><input type="text" value={d.potm || ""} onChange={(e) => setDraft(m.id, "potm", e.target.value)} placeholder="e.g. K. Prasad" data-testid={`match-potm-${index}`} /></div>
              </div>
              <div className="inn-grid inn-grid-two">
                <div className="field"><label>Match card</label>
                  <div className="page-pills" style={{ margin: "0 0 6px" }}>
                    <button type="button" className={`gallery-tab ${(m.card_mode || "auto") === "auto" ? "active" : ""}`} onClick={() => setCardMode(m.id, "auto")} data-testid={`card-mode-auto-${index}`}>Auto (logos)</button>
                    <button type="button" className={`gallery-tab ${m.card_mode === "custom" ? "active" : ""}`} onClick={() => setCardMode(m.id, "custom")} data-testid={`card-mode-custom-${index}`}>Custom image</button>
                  </div>
                  <input type="file" accept="image/*" onChange={(e) => uploadCard(m.id, e.target.files[0])} data-testid={`match-card-${index}`} />
                  {m.card_image && <img className="reg-photo match-card-thumb" src={`${API}/gallery/file/${m.card_image}`} alt="Match card" data-testid={`match-card-preview-${index}`} />}
                </div>
                <div className="field"><label>Sponsor logo (shown on card)</label>
                  <input type="file" accept="image/*" onChange={(e) => uploadSponsor(m.id, e.target.files[0])} data-testid={`match-sponsor-${index}`} />
                  {m.sponsor_logo && <img className="reg-photo" src={`${API}/gallery/file/${m.sponsor_logo}`} alt="Sponsor" data-testid={`match-sponsor-preview-${index}`} />}
                </div>
              </div>
              <div className="inn-grid inn-grid-two">
                <div className="field"><label>Match no.</label><input type="text" value={d.match_no || ""} onChange={(e) => setDraft(m.id, "match_no", e.target.value)} data-testid={`match-no-${index}`} /></div>
                <div className="field"><label>Time</label><input type="text" value={d.time || ""} onChange={(e) => setDraft(m.id, "time", e.target.value)} placeholder="e.g. 7:00 PM" data-testid={`match-time-${index}`} /></div>
              </div>
              {scorers.length > 0 && <div className="field"><label>Assigned scorers (they can only score this match)</label>
                <div className="scorer-assign">{scorers.map((s) => <label key={s.id} className="scorer-check"><input type="checkbox" checked={(m.scorers || []).includes(s.id)} onChange={() => toggleScorer(m.id, s.id)} data-testid={`assign-${index}-${s.id}`} /> {s.name} ({s.email})</label>)}</div>
              </div>}
              <div className="match-row-actions">
                <Link className="button button-red" to={`/scorer?match=${m.id}`} data-testid={`match-scorer-${index}`}>Open scorer</Link>
                <button type="button" className="button button-outline" onClick={() => saveMatch(m.id)} data-testid={`match-save-${index}`}>Save score</button>
                <button type="button" className="button button-outline" onClick={() => showAudit(m.id)} data-testid={`match-history-${index}`}>{auditFor === m.id ? "Hide history" : "History"}</button>
                <div className="comm-add">
                  <input type="text" value={commText[m.id] || ""} onChange={(e) => setCommText((c) => ({ ...c, [m.id]: e.target.value }))} placeholder="Add commentary — e.g. 19.3: SIX! Over long-on" data-testid={`match-comm-input-${index}`} />
                  <button type="button" className="button button-outline" onClick={() => addCommentary(m.id)} data-testid={`match-comm-add-${index}`}>Post</button>
                </div>
              </div>
              {auditFor === m.id && <div className="comm-preview" data-testid={`audit-${index}`}>
                {auditEntries.length === 0 ? <p>No scoring activity recorded yet.</p> : auditEntries.map((entry, i) => <p key={i}><b>{entry.action}</b> — {entry.detail} <em>({entry.by}, {(entry.at || "").slice(0, 16).replace("T", " ")})</em></p>)}
              </div>}
              {(d.commentary || []).length > 0 && <div className="comm-preview">{(d.commentary || []).slice(0, 3).map((entry, i) => <p key={i}>{entry.text}</p>)}</div>}
            </div>; })}</div>
          )}
        </div>}
        {tab === "pages" && <div className="admin-card">
          <div className="reg-head"><h3>Policy pages</h3></div>
          <div className="page-pills">{PAGE_LIST.map(([key, label]) => <button key={key} className={`gallery-tab ${pageSlug === key ? "active" : ""}`} onClick={() => setPageSlug(key)} data-testid={`page-pill-${key}`}>{label}</button>)}</div>
          <Field label="Page title" value={(content.pages?.[pageSlug] ?? PAGE_DEFAULTS[pageSlug]).title} onChange={(v) => setPageField(pageSlug, "title", v)} testid="edit-page-title" />
          <div className="field"><label>Content — blank line between paragraphs, start a line with ## for a sub-heading</label>
            <textarea rows={14} value={(content.pages?.[pageSlug] ?? PAGE_DEFAULTS[pageSlug]).body} onChange={(e) => setPageField(pageSlug, "body", e.target.value)} data-testid="edit-page-body" />
          </div>
          <p className="reg-empty">Public link: <a href={PAGE_LIST.find(([k]) => k === pageSlug)[2]} target="_blank" rel="noreferrer" data-testid="page-public-link">{PAGE_LIST.find(([k]) => k === pageSlug)[2]}</a></p>
        </div>}
        {tab === "gallery" && <div className="admin-card">
          <div className="reg-head">
            <h3>Gallery manager {gallery ? `· ${gallery.count}` : ""}</h3>
          </div>
          <form className="gallery-upload" onSubmit={uploadGalleryItem} data-testid="gallery-upload-form">
            <div className="field"><label>Type</label>
              <select value={gKind} onChange={(e) => setGKind(e.target.value)} data-testid="gallery-kind-select">
                <option value="photo">Photo</option>
                <option value="video">Video</option>
                <option value="interview">Interview</option>
              </select>
            </div>
            <div className="field"><label>Title</label><input type="text" value={gTitle} onChange={(e) => setGTitle(e.target.value)} placeholder="e.g. RMG ground, auction highlights" data-testid="gallery-title-input" /></div>
            <div className="field"><label>File {gKind === "photo" ? "(image, max 8 MB)" : "(video, max 100 MB)"}</label><input type="file" accept={gKind === "photo" ? "image/*" : "video/*"} onChange={(e) => setGFile(e.target.files[0] || null)} data-testid="gallery-file-input" /></div>
            <button className="button button-red" type="submit" disabled={gBusy} data-testid="gallery-upload-button">{gBusy ? "Uploading…" : "Upload to gallery"}</button>
          </form>
          {gError && <p className="auth-error" data-testid="gallery-upload-error" style={{ marginTop: 10 }}>{gError}</p>}
          {!gallery || gallery.count === 0 ? <p className="reg-empty" data-testid="gallery-empty" style={{ marginTop: 16 }}>No uploads yet — the five built-in KPL photos always show on the public gallery.</p> : (
            <div className="gallery-items" style={{ marginTop: 16 }}>{gallery.items.map((item, index) => <div className="reg-row gallery-item-row" key={item.id} data-testid={`gallery-item-${index}`}>
              <button className="row-del" onClick={() => deleteGalleryItem(item.id)} data-testid={`delete-gallery-${index}`} aria-label="Delete item"><Trash2 size={15} /></button>
              <div><label>Type</label><b className={`gallery-kind kind-${item.kind}`}>{item.kind}</b></div>
              <div><label>Title</label><span>{item.title}</span></div>
              <div><label>Preview</label>{item.kind === "photo" ? <img className="reg-photo" src={`${API}/gallery/file/${item.storage_path}`} alt={item.title} /> : <video className="reg-photo" src={`${API}/gallery/file/${item.storage_path}`} preload="metadata" muted />}</div>
              <div><label>Uploaded On</label><span>{(item.created_at || "").slice(0, 10)}</span></div>
            </div>)}</div>
          )}
        </div>}
      </div>
      {!readOnlyTab && <div className="admin-save-bar">
        <button className="button button-red" onClick={save} disabled={busy} data-testid="admin-save-button">{busy ? "Saving…" : "Save changes"}</button>
        {saved && <span className="saved-note" data-testid="admin-saved-note">Saved — live on the site.</span>}
      </div>}
    </div>
  </div>;
}
